import { useState } from 'react';
import { Palette, Monitor, Moon, Sun, Eye, Zap } from 'lucide-react';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { useThemeSettings } from '../../stores/useSettingsStore.js';

const CustomizationTab = () => {
  const { theme, setTheme } = useThemeSettings();
  const [settings, setSettings] = useState({
    theme: theme === 'classic' ? 'dark' : 'light',
    accentColor: 'purple',
    fontSize: 'medium',
    compactMode: false,
    animations: true,
    autoHideNavbar: false,
    showLineNumbers: true,
    wordWrap: true
  });

  const themes = [
    { id: 'dark', name: 'Dark', icon: Moon, preview: 'bg-slate-900' },
    { id: 'light', name: 'Light', icon: Sun, preview: 'bg-white' },
    { id: 'auto', name: 'Auto', icon: Monitor, preview: 'bg-gradient-to-r from-slate-900 to-white' }
  ];

  const accentColors = [
    { id: 'purple', name: 'Purple', color: 'bg-purple-500' },
    { id: 'blue', name: 'Blue', color: 'bg-blue-500' },
    { id: 'green', name: 'Green', color: 'bg-green-500' },
    { id: 'orange', name: 'Orange', color: 'bg-orange-500' },
    { id: 'pink', name: 'Pink', color: 'bg-pink-500' },
    { id: 'cyan', name: 'Cyan', color: 'bg-cyan-500' }
  ];

  const fontSizes = [
    { id: 'small', name: 'Small', size: 'text-sm' },
    { id: 'medium', name: 'Medium', size: 'text-base' },
    { id: 'large', name: 'Large', size: 'text-lg' }
  ];

  const handleSettingChange = (key: string, value: string | boolean) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    
    if (key === 'theme') {
      const themeValue = value === 'dark' ? 'classic' : 'hive';
      setTheme(themeValue);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Customization</h2>
        <p className="text-slate-400 mt-1">Personalize your Beehive workspace appearance</p>
      </div>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Palette size={20} className="text-brand-accent" />
            <span>Theme & Colors</span>
          </CardTitle>
          <CardDescription>Choose your preferred theme and accent colors</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">Theme</label>
            <div className="grid grid-cols-3 gap-3">
              {themes.map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => handleSettingChange('theme', theme.id)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    settings.theme === theme.id
                      ? 'border-brand-primary bg-brand-primary/10'
                      : 'border-slate-600 hover:border-slate-500'
                  }`}
                >
                  <div className={`w-full h-12 rounded-md mb-2 ${theme.preview}`} />
                  <div className="flex items-center justify-center space-x-2">
                    <theme.icon size={16} className="text-slate-400" />
                    <span className="text-sm text-slate-300">{theme.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">Accent Color</label>
            <div className="grid grid-cols-6 gap-3">
              {accentColors.map((color) => (
                <button
                  key={color.id}
                  onClick={() => handleSettingChange('accentColor', color.id)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    settings.accentColor === color.id
                      ? 'border-brand-primary'
                      : 'border-slate-600 hover:border-slate-500'
                  }`}
                >
                  <div className={`w-full h-8 rounded-md ${color.color}`} />
                  <div className="text-xs text-slate-400 mt-1">{color.name}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">Font Size</label>
            <div className="grid grid-cols-3 gap-3">
              {fontSizes.map((size) => (
                <button
                  key={size.id}
                  onClick={() => handleSettingChange('fontSize', size.id)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    settings.fontSize === size.id
                      ? 'border-brand-primary bg-brand-primary/10'
                      : 'border-slate-600 hover:border-slate-500'
                  }`}
                >
                  <div className={`${size.size} text-slate-300 mb-1`}>Aa</div>
                  <div className="text-xs text-slate-400">{size.name}</div>
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Eye size={20} className="text-brand-accent" />
            <span>Interface Options</span>
          </CardTitle>
          <CardDescription>Customize your workspace layout and behavior</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <Monitor size={20} className="text-brand-accent" />
              <div>
                <div className="text-sm font-medium text-brand-text">Compact Mode</div>
                <div className="text-xs text-slate-400">Reduce spacing and padding</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.compactMode}
                onChange={(e) => handleSettingChange('compactMode', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <Zap size={20} className="text-brand-accent" />
              <div>
                <div className="text-sm font-medium text-brand-text">Animations</div>
                <div className="text-xs text-slate-400">Enable smooth transitions and effects</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.animations}
                onChange={(e) => handleSettingChange('animations', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <Eye size={20} className="text-brand-accent" />
              <div>
                <div className="text-sm font-medium text-brand-text">Auto-hide Navbar</div>
                <div className="text-xs text-slate-400">Hide navigation when not in use</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.autoHideNavbar}
                onChange={(e) => handleSettingChange('autoHideNavbar', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Monitor size={20} className="text-brand-accent" />
            <span>Editor Preferences</span>
          </CardTitle>
          <CardDescription>Customize the code editor appearance</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 bg-brand-accent rounded flex items-center justify-center">
                <span className="text-xs text-white font-mono">1</span>
              </div>
              <div>
                <div className="text-sm font-medium text-brand-text">Show Line Numbers</div>
                <div className="text-xs text-slate-400">Display line numbers in editor</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.showLineNumbers}
                onChange={(e) => handleSettingChange('showLineNumbers', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-5 h-5 bg-brand-accent rounded flex items-center justify-center">
                <span className="text-xs text-white">↵</span>
              </div>
              <div>
                <div className="text-sm font-medium text-brand-text">Word Wrap</div>
                <div className="text-xs text-slate-400">Wrap long lines in editor</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.wordWrap}
                onChange={(e) => handleSettingChange('wordWrap', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end space-x-3">
        <Button variant="ghost">Reset to Defaults</Button>
        <Button>Save Preferences</Button>
      </div>
    </div>
  );
};

export default CustomizationTab;
